import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateWithoutTZ, getSelectedDateRangeString } from '@common/utils';
import { Box } from '@base-app';
import { DateRangeDropdown, DateRangeSelection, DateRangeOptionId } from '@builder';
import { COMMON_TRANSLATION } from '@constants/index';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';

import { StyledDateRange, StyledDateFilter, StyledSelectedRangeText } from './DateFilter.styles';
import { DateFilterProps } from './DateFilter.types';

const DateFilter: FC<DateFilterProps> = ({
  buttonStyles = {},
  css = {},
  onDateRangeChange,
  rangeTextStyles = {},
  selectedDateRange,
  storeEndDate,
  storeStartDate,
  layout,
  showSelectedRangeText = true,
}) => {
  const { t } = useTranslation([STUDY_INSIGHTS_TRANSLATION, COMMON_TRANSLATION]);
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeSelection | undefined>(selectedDateRange);
  const [dateRangeDisplay, setDateRangeDisplay] = useState<string>();

  useEffect(() => {
    if (dateRangeFilter !== undefined) onDateRangeChange(dateRangeFilter);
  }, [dateRangeFilter]);

  useEffect(() => {
    if (!selectedDateRange) {
      return;
    }
    const { startDate, endDate, selectedDateRange: dateRangeOption } = selectedDateRange;
    if (dateRangeOption === DateRangeOptionId.ALL_TIME) {
      setDateRangeDisplay(t('date_range_dropdown_options.all_time', { ns: COMMON_TRANSLATION }));
    } else if (startDate !== undefined && endDate !== undefined) {
      setDateRangeDisplay(getSelectedDateRangeString(startDate, endDate));
    }
  }, [selectedDateRange]);

  const handleDateRangeSelect = (dateRangeOption: DateRangeSelection): void => {
    let selectionStartDate = dateRangeOption?.startDate;
    let selectionEndDate = dateRangeOption?.endDate;

    // if filter is All Time, select store start date
    if (dateRangeOption?.selectedDateRange === DateRangeOptionId.ALL_TIME) {
      if (storeStartDate) {
        selectionStartDate = new Date(storeStartDate);
      }
      if (storeEndDate) {
        selectionEndDate = new Date(storeEndDate);
      } else {
        selectionEndDate = new Date();
      }
    }
    setDateRangeFilter({
      ...dateRangeOption,
      startDate: selectionStartDate,
      endDate: selectionEndDate,
    });
  };

  return (
    <StyledDateFilter css={css}>
      {showSelectedRangeText && (
        <StyledSelectedRangeText css={rangeTextStyles}>
          <Box>{t('selected_date_range.label')}</Box>
          <StyledDateRange>{dateRangeDisplay}</StyledDateRange>
        </StyledSelectedRangeText>
      )}
      <DateRangeDropdown
        buttonStyles={buttonStyles}
        value={selectedDateRange}
        dateRangeErrorTitle={t('selected_date_range.error.title')}
        dateRangeErrorDescription={t('selected_date_range.error.description', {
          dateRange:
            storeStartDate &&
            storeEndDate &&
            getSelectedDateRangeString(
              new Date(getDateWithoutTZ(storeStartDate)),
              new Date(getDateWithoutTZ(storeEndDate)),
            ),
        })}
        maxDate={storeEndDate ? new Date(getDateWithoutTZ(storeEndDate)) : undefined}
        minDate={storeStartDate ? new Date(getDateWithoutTZ(storeStartDate)) : undefined}
        onSelect={handleDateRangeSelect}
        layout={layout}
      />
    </StyledDateFilter>
  );
};

export default DateFilter;
